import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { getFlightLandingPageRegionContent } from './FlightLandingPageContent'
import Pane from 'components/Common/Pane'
import GeoContext from 'contexts/geoContext'
import Image from 'components/Common/Image'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import FlightsSearch from 'components/Flights/FlightsSearch/FlightsSearch'
import FlightsCreditView from 'components/Flights/CreditRebooking/FlightsCreditView'
import { isFlightsCredit } from 'selectors/flightsSelectors'
import { flightsImageId, LUX_PLUS_POST_PURCHASE_FLIGHT_BANNER_SOURCE } from 'constants/flight'
import GlobalSearchHeroContainer from 'components/Search/GlobalSearch/GlobalSearchHeroContainer'
import GlobalSearchContainerPane, { GlobalSearchContainerPaneContent } from 'components/Search/GlobalSearch/GlobalSearchContainerPane'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { flightsLandingPageView } from 'analytics/eventDefinitions/index'
import { trackClientEvent } from 'analytics/analytics'
import { fireNonInteractionEvent } from 'api/googleTagManager'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import useFlightDealLocations from 'home/hooks/useFlightDealLocations'
import FlightDealsCarousel from 'home/components/FlightDeals/FlightDealsCarousel/FlightDealsCarousel'
import { FlightCampaigns } from 'home/components/FlightCampaigns/FlightCampaigns'
import { useFlightCampaigns } from 'hooks/Flights/useFlightCampaigns'
import { useShowFlightCampaignsOrDeals } from 'hooks/Flights/useCampaignsOrDeals'
import useRecentFlightSearches from 'hooks/Flights/useRecentFlightSearches'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useAppSelector } from 'hooks/reduxHooks'
import useDefaultAirport from 'hooks/useDefaultAirport'
import { mapAirportToAirportLocation } from 'lib/flights/flightUtils'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import PopularFlightsSection from 'components/Flights/PopularFlights/PopularFlightsSection'
import { useExcludedFlightDestinations } from 'hooks/Flights/useExcludedFlightDestinations'
import useLuxPlusFreePreviewUtils from 'luxPlus/hooks/useLuxPlusFreePreviewUtils'
import { getHasLuxPlusInAnyRegion, getIsEnabledFlightsLuxPlusPricing, getIsEnabledFlightsLuxPlusPreview, getIsFreePreviewLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import FlightsLuxPlusPreviewPrompt from './FlightsLuxPlusPreviewPrompt'
import FlightsLuxPlusMemberBanner from './FlightsLuxPlusMemberBanner'

const AirlineLogos = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${rem(48)};
  justify-items: center;
  justify-content: center;
  align-items: center;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(4, ${rem(120)});
  }
`

const ContentContainer = styled(LayoutContainer)`
  padding-top: ${rem(48)};
  padding-bottom: ${rem(48)};

  > * + * {
    margin-top: ${rem(20)};
  }
`

const FlightDealBlock = styled.div`
  padding: ${rem(48)} 0;
`

type MatchProps = { dealCategory?: string }
interface Props {
  isFlightsCredit: boolean
}

function FlightsSearchLandingPage(props: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const content = useMemo(() => {
    const allRegionContent = getFlightLandingPageRegionContent(intl)

    return allRegionContent[currentRegionCode] || allRegionContent.AU
  }, [currentRegionCode, intl])
  const { isFlightsCredit } = props
  const { dealCategory } = useParams<MatchProps>()
  const location = useLocation()
  const [flightDeals, fetching] = useFlightDeals(dealCategory)
  const [campaigns, fetchingCampaigns] = useFlightCampaigns()
  const [airports, defaultAirport] = useFlightDealLocations(dealCategory)
  const [selectedAirport, setAirport] = useState<App.Airport | undefined>()
  const campaignsOrDeals = useShowFlightCampaignsOrDeals()
  const recentSearchesData = useRecentFlightSearches()
  const isMobileDevice = useIsMobileDevice()
  const isMobileViewport = useScreenSizeOnly('mobile')
  const isMobileLayout = isMobileDevice || isMobileViewport
  const { showFreePreviewSignup } = useLuxPlusFreePreviewUtils()
  const isFlightsLuxPlusPricingEnabled = useAppSelector(getIsEnabledFlightsLuxPlusPricing)
  const isEnabledFlightsLuxPlusPreview = useAppSelector(getIsEnabledFlightsLuxPlusPreview)
  const hasPaidLuxPlusMembership = useAppSelector(getHasLuxPlusInAnyRegion)
  const isFreePreviewLuxPlusMember = useAppSelector(getIsFreePreviewLuxPlusMember)

  const mobileHeroTitle = intl.formatMessage({
    id: 'g2L8FyPRAH2NZDy20tc2',
    defaultMessage: 'Your journey starts here.',
    description: 'Mobile hero title on flights search landing page on the promotional landing page',
  })
  const desktopHeroTitle = intl.formatMessage({
    id: 'BJPULOQmTm92gZvV17jE',
    defaultMessage: 'Your journey starts here. Search flights',
    description: 'Desktop hero title on flights search landing page on the promotional landing page',
  })

  const prefillAirport = useDefaultAirport()
  const prefillDepartureAirport = !isMobileLayout && prefillAirport
    ? mapAirportToAirportLocation(prefillAirport)
    : undefined

  const mainContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fireNonInteractionEvent(flightsLandingPageView())
  }, [])

  useEffect(() => {
    const source = new URLSearchParams(location.search).get('source')

    if (source === LUX_PLUS_POST_PURCHASE_FLIGHT_BANNER_SOURCE) {
      trackClientEvent({
        action: 'start',
        subject: 'standalone_flight_search_from_post_purchase_banner',
        category: 'flight_search',
        type: 'interaction',
      })
    }
  }, [location.search])

  const [locations] = useFlightDealLocations()

  const dealAirport = selectedAirport ?? defaultAirport
  const excludedDestinations = useExcludedFlightDestinations({
    campaignsOrDeals,
    flightDeals,
    dealAirport,
  })

  const showFlightDeals = fetching || (!fetching && flightDeals.length > 0)
  const showAirlineLogos = !fetching && !isFlightsCredit && !showFlightDeals
  const showLuxPlusPreviewPrompt = showFreePreviewSignup &&
    isFlightsLuxPlusPricingEnabled &&
    !hasPaidLuxPlusMembership &&
    !isFreePreviewLuxPlusMember &&
    isEnabledFlightsLuxPlusPreview
  const showLuxPlusMemberBanner = isFlightsLuxPlusPricingEnabled &&
    (hasPaidLuxPlusMembership || (isFreePreviewLuxPlusMember && isEnabledFlightsLuxPlusPreview))

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage}>
      <GlobalSearchHeroContainer heroImageId={flightsImageId} title={isMobileLayout ? mobileHeroTitle : desktopHeroTitle}>
        <GlobalSearchContainerPane displayMobile={isMobileLayout}>
          <GlobalSearchContainerPaneContent>
            <FlightsSearch
              fromFlightsLanding
              recentSearchesData={recentSearchesData}
              departureAirport={prefillDepartureAirport}
              autoFocusArrival={!isMobileLayout}
            />
          </GlobalSearchContainerPaneContent>
        </GlobalSearchContainerPane>
      </GlobalSearchHeroContainer>

      {showLuxPlusPreviewPrompt && <FlightsLuxPlusPreviewPrompt />}
      {showLuxPlusMemberBanner && <FlightsLuxPlusMemberBanner />}

      <Pane ref={mainContentRef}>
        {showAirlineLogos && <ContentContainer size="md">
          <Heading align="center" as="h2" variant="heading3">{intl.formatMessage({
            id: 'SfywfKEZ33y2MtWQD-8m',
            defaultMessage: 'Fly with the world’s best airlines',
            description: 'Heading for airline logos section on flights search landing page',
          })}
          </Heading>
          <BodyText variant="large" align="center" as="p">
            {intl.formatMessage({
              id: 'qwEqE-9-Vd5M6ErVzFhl',
              defaultMessage: 'Whether you are a loyal frequent flyer member, prefer a select airline or simply want the best value fare, we have you covered.',
              description: 'Description text for airline logos section on flights search landing page',
            })}
          </BodyText>
          <AirlineLogos>
            {content.airlineLogos?.map((airline) => (
              <Image
                id={airline.id}
                alt={airline.alt}
                width={airline.width}
                key={airline.id}
              />
            ))}
          </AirlineLogos>
        </ContentContainer>}

        <FlightsCreditView />

        {(!isAgentHubEnabled && campaignsOrDeals !== 'none') && (
          <>
            {campaignsOrDeals === 'campaigns' && campaigns.length > 0 && (
              <FlightDealBlock>
                <FlightCampaigns campaigns={campaigns} loading={fetchingCampaigns} />
              </FlightDealBlock>
            )}

            {campaignsOrDeals === 'deals' && flightDeals.length > 0 && (
              <FlightDealBlock>
                <FlightDealsCarousel
                  title={locations.length > 1
                    ? intl.formatMessage({ id: 'jS9cAjV_hYMPwqtiehek', defaultMessage: 'Best flight deals from', description: 'Title for flight deals carousel when multiple departure locations are available' })
                    : intl.formatMessage({ id: 'STHG8k6VxXmFiXKTrhON', defaultMessage: 'Best flight deals', description: 'Title for flight deals carousel in the flight search or booking flow' })}
                  airports={airports}
                  flightDeals={flightDeals}
                  onAirportChange={setAirport}
                  defaultAirport={defaultAirport}
                  selectedAirport={selectedAirport}
                  loading={fetching}
                />
              </FlightDealBlock>
            )}
          </>
        )}
        {!fetching && <PopularFlightsSection excludedDestinations={excludedDestinations} />}
      </Pane>
    </AnalyticsPageContext.Provider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    isFlightsCredit: isFlightsCredit(state),
  }
}

export default connect(mapStateToProps)(FlightsSearchLandingPage)
